import {
  Box,
  Typography as Typo,
  styled,
  Link,
  FormControl,
  InputAdornment,
  FormHelperText as MuiFormHelperText,
  IconButton,
  TypographyProps,
} from '@mui/material';
import Input from '@jobseeker/lotus/elements/Input';
import {Image} from '@jobseeker/lotus/components/Image';
import {Eye, HiddenEye} from '@jobseeker/lotus/assets/icons';
import {Dispatch, ReactNode, SetStateAction, useState} from 'react';
import {VersionVariants} from '@jobseeker/lotus/enums';
import {useFormik} from 'formik';
import Button from '@jobseeker/lotus/elements/ButtonV2';
import authService from '@jobseeker/common-core/services/auth.service';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import userService from '@jobseeker/common-core/services/user.service';
import {validationSchemaPassword} from '../../../../helpers/formValidator';

const Typography = styled(Typo)<TypographyProps>(({}) => ({
  display: 'flex',
  alignItems: 'center',
}));

const FormHelperText = styled(MuiFormHelperText)(() => ({
  position: 'absolute',
  bottom: -18,
}));

interface PasswordComponentProps {
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
  setTitle: Dispatch<SetStateAction<string>>;
  setSubTitle?: Dispatch<SetStateAction<string>>;
  setDialogContent: Dispatch<SetStateAction<ReactNode>>;
}

export const PasswordComponent = ({
  setDialogOpen,
  setTitle,
  setSubTitle,
  setDialogContent,
}: PasswordComponentProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const {profile} = useSelector((state: AppState) => state.user);

  const formik = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: validationSchemaPassword,
    onSubmit: async values => {
      const response = await userService.twoFaDisable(values);
      if (response?.error) {
        formik.setFieldError('password', response.error.message);
        return;
      }
      setTitle('2-Step Verification Disabled');
      if (setSubTitle)
        setSubTitle(
          'You will no longer receive a verification code when you login to your iimjobs.com account. You can enable this anytime by visiting your settings page'
        );
      setDialogContent(
        <>
          <Box display={'flex'} gap={1.5} mt={3} mb={2}>
            <Button
              onClick={handleOkayClick}
              color="secondary"
              variant="contained"
            >
              Okay
            </Button>
          </Box>
        </>
      );
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  let ref = '';
  if (window && window.location) {
    const urlParams = new URLSearchParams(window.location.search);
    ref = urlParams.get('ref') || '';
  }

  const dispatch = useDispatch();

  const handleForgotPass = async (email: string) => {
    const resInfo = await authService.forgotPassword('forgot', {
      email,
    });

    setTitle(
      `We’ve sent an email to ${profile?.email}.Click the link in the email to rest your password.`
    );
    if (setSubTitle)
      setSubTitle(
        'If you don’t see the email, check other places it might be, like your junk, spam, social, or other folders.'
      );
    setDialogContent(
      <>
        <Box display={'flex'} gap={1.5} mt={3} mb={2}>
          <Button
            onClick={() => {
              setDialogOpen(false);
            }}
            color="secondary"
            variant="contained"
          >
            Okay
          </Button>
        </Box>
      </>
    );
  };

  const handleOkayClick = () => {
    dispatch({
      type: 'User/set_user_profile',
      payload: {
        settings: {
          twoFactorAuth: 0,
        },
      },
    });
    setDialogOpen(false);
  };

  return (
    <>
      <Box
        component="form"
        noValidate
        onSubmit={event => {
          event.preventDefault();
          formik.handleSubmit();
        }}
      >
        <FormControl
          sx={{width: '100%', mt: 0.5}}
          error={formik.touched.password && Boolean(formik.errors.password)}
        >
          <Input
            variant={VersionVariants.VERSION_THREE}
            type={showPassword ? 'text' : 'Password'}
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            error={formik.touched.password && Boolean(formik.errors.password)}
            placeholder="Enter the password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <Image
                        src={Eye}
                        width={15}
                        height={15}
                        alt="show password"
                      />
                    ) : (
                      <Image
                        src={HiddenEye}
                        width={15}
                        height={15}
                        alt="hide password"
                      />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {formik.touched.password && Boolean(formik.errors.password) && (
            <FormHelperText>{formik.errors.password}</FormHelperText>
          )}
        </FormControl>

        <Typography
          display={'flex'}
          justifyContent={'flex-end'}
          fontSize={12}
          fontWeight={400}
        >
          <Link
            sx={{
              fontSize: '12px',
              fontWeight: '400',
              padding: '0px',
              mt: '2px',
              color: 'var(--brand-color, #49966E)',
              display: 'flex',
              textDecoration: 'none',
              justifyContent: 'flex-start',
              '&:hover': {
                cursor: 'pointer',
                backgroundColor: 'transparent',
              },
            }}
            onClick={() => handleForgotPass(profile.email)}
          >
            Forgot Password?
          </Link>
        </Typography>
        <Box display={'flex'} gap={1.5} mt={2.625} mb={2}>
          <Button
            disabled={!formik.isValid}
            type="submit"
            color="secondary"
            variant="contained"
            sx={{px: 3, py: 1}}
          >
            Disable
          </Button>

          <Button
            sx={{px: 3, py: 1}}
            onClick={() => setDialogOpen(false)}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </>
  );
};
